import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { AIProvider, AIModelPricing as AIModelPricingType, AIPrompt } from '@/services/admin-api';
import { createPromptSchema, createPromptVersionSchema, type CreatePromptInput, type CreatePromptVersionInput } from '@/lib/schemas/ai-schemas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface PromptFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreatePromptInput | CreatePromptVersionInput) => void;
  isPending: boolean;
  providers: AIProvider[];
  pricing: AIModelPricingType[];
  mode: 'create' | 'newVersion';
  prefill?: AIPrompt | null;
}

export function PromptFormDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
  providers,
  pricing,
  mode,
  prefill,
}: PromptFormDialogProps) {
  const isCreate = mode === 'create';
  const schema = isCreate ? createPromptSchema : createPromptVersionSchema;

  const form = useForm<z.input<typeof createPromptSchema>, unknown, CreatePromptInput>({
    resolver: zodResolver(schema as typeof createPromptSchema),
    defaultValues: {
      id: '',
      providerId: '',
      model: '',
      systemPrompt: '',
      userPromptTemplate: '',
      temperature: 0.7,
      maxTokens: undefined,
      topP: undefined,
      schemaRef: '',
    },
  });

  const watchedProviderId = form.watch('providerId');
  const modelsForProvider = pricing.filter((p) => p.providerId === watchedProviderId);

  // Reset model when provider changes
  useEffect(() => {
    const current = form.getValues('model');
    const available = pricing.filter((p) => p.providerId === watchedProviderId).map((p) => p.model);
    if (current && !available.includes(current)) {
      form.setValue('model', '');
    }
  }, [watchedProviderId, pricing, form]);

  // Prefill on open
  useEffect(() => {
    if (open && prefill) {
      form.reset({
        id: isCreate ? '' : prefill.id,
        providerId: prefill.providerId,
        model: prefill.model,
        systemPrompt: prefill.systemPrompt,
        userPromptTemplate: prefill.userPromptTemplate ?? '',
        temperature: prefill.temperature ?? 0.7,
        maxTokens: prefill.maxTokens ?? undefined,
        topP: prefill.topP ?? undefined,
        schemaRef: prefill.schemaRef ?? '',
      });
    } else if (open && !prefill) {
      form.reset({
        id: '',
        providerId: '',
        model: '',
        systemPrompt: '',
        userPromptTemplate: '',
        temperature: 0.7,
        maxTokens: undefined,
        topP: undefined,
        schemaRef: '',
      });
    }
  }, [open, prefill, form, isCreate]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isCreate ? 'New Prompt' : 'New Version'}</DialogTitle>
          <DialogDescription>
            {isCreate
              ? 'Create a new prompt configuration.'
              : `Create a new version for "${prefill?.id}".`}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit((data) => onSubmit(data))} className="space-y-4">
          {isCreate && (
            <div className="space-y-2">
              <Label htmlFor="pf-id">Prompt ID</Label>
              <Input id="pf-id" placeholder="resume-parser" {...form.register('id')} />
              {form.formState.errors.id && (
                <p className="text-sm text-destructive">{form.formState.errors.id.message}</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Provider</Label>
              <Controller
                control={form.control}
                name="providerId"
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.filter((p) => p.isActive).map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {form.formState.errors.providerId && (
                <p className="text-sm text-destructive">{form.formState.errors.providerId.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Model</Label>
              <Controller
                control={form.control}
                name="model"
                render={({ field }) => (
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={!watchedProviderId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={watchedProviderId ? 'Select model' : 'Select provider first'} />
                    </SelectTrigger>
                    <SelectContent>
                      {modelsForProvider.map((m) => (
                        <SelectItem key={m.model} value={m.model}>{m.model}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {form.formState.errors.model && (
                <p className="text-sm text-destructive">{form.formState.errors.model.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="pf-system">System Prompt</Label>
            <Textarea
              id="pf-system"
              className="min-h-[160px] font-mono text-sm"
              placeholder="You are a helpful assistant..."
              {...form.register('systemPrompt')}
            />
            {form.formState.errors.systemPrompt && (
              <p className="text-sm text-destructive">{form.formState.errors.systemPrompt.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="pf-user">User Prompt Template (optional)</Label>
            <Textarea
              id="pf-user"
              className="min-h-[80px] font-mono text-sm"
              placeholder="Parse the following resume: {{resumeText}}"
              {...form.register('userPromptTemplate')}
            />
          </div>

          <div className="grid grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="pf-temp">Temperature</Label>
              <Input id="pf-temp" type="number" step="0.1" min="0" max="2" {...form.register('temperature')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pf-tokens">Max Tokens</Label>
              <Input id="pf-tokens" type="number" {...form.register('maxTokens')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pf-topp">Top P</Label>
              <Input id="pf-topp" type="number" step="0.1" min="0" max="1" {...form.register('topP')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pf-schema">Schema Ref</Label>
              <Input id="pf-schema" placeholder="optional" {...form.register('schemaRef')} />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Saving...' : isCreate ? 'Create' : 'Create Version'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
